import { AdminRemovalDialog } from './AdminRemovalDialog';

import { stableRecordKey } from '../../lib/rowIdentity';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Trash2, Edit, Upload, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle,
  Bold, Italic, Underline, List, ListOrdered, Link2, Image as ImageIcon,
  Send, Mail, Users as UsersIcon, Activity, Receipt, FileText, Camera, QrCode, Calendar, StopCircle, Sparkles as Sparkle, BadgeCheck, UserPlus
  , Download } from 'lucide-react';
import { DateRangeFilter, emptyDateRange, inDateRange, describeDateRange, downloadCsv, fmtDateGB, fmtMoney, rangeSlug } from '../../components/DateRangeFilter';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { AddressLookup } from '../../components/AddressLookup';
import { toast } from 'sonner';
import { api } from '../../App';

import { sanitizeHtml, setSafeEditorHtml, safeContentUrl } from '../../lib/safeHtml';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ============================================================
   RICH TEXT EDITOR (font sizes, styles, images, lists, links)
   ============================================================ */
export const RichTextEditor = ({ value, onChange, token }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && sanitizeHtml(editor.innerHTML) !== sanitizeHtml(value)) setSafeEditorHtml(editor, value);
  }, [value]);

  const exec = (cmd, arg = null) => {
    if (cmd === 'createLink' || cmd === 'insertImage') {
      arg = safeContentUrl(arg, { image: cmd === 'insertImage' });
      if (!arg) { toast.error('Use a valid http, https, mailto or telephone link. Images must use http or https.'); return; }
    }
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(sanitizeHtml(editorRef.current?.innerHTML || ''));
  };

  const paste = (event) => {
    event.preventDefault();
    const clipboard = event.clipboardData;
    const html = clipboard.getData('text/html');
    if (html) exec('insertHTML', sanitizeHtml(html));
    else exec('insertText', clipboard.getData('text/plain'));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) { toast.error('Image must be under 30MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/admin/upload?category=newsletters`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      exec('insertImage', `${BACKEND_URL}${data.url}`);
      toast.success('Image inserted');
    } catch (err) { toast.error(err.message); }
    setUploading(false);
    e.target.value = '';
  };

  const ToolBtn = ({ onClick, title, children, testid }) => (
    <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} title={title}
      className="p-1.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] transition-colors"
      data-testid={testid}>
      {children}
    </button>
  );

  return (
    <div className="border border-[hsl(var(--border))] rounded-lg overflow-hidden" data-testid="rich-text-editor">
      <div className="flex items-center gap-1 flex-wrap p-2 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)]">
        <select onChange={e => { if (e.target.value) { exec('formatBlock', e.target.value); e.target.value = ''; } }}
          className="text-xs rounded border border-[hsl(var(--input))] px-1.5 py-1 bg-white" defaultValue=""
          data-testid="editor-heading-select">
          <option value="" disabled>Style</option>
          <option value="h2">Heading Large</option>
          <option value="h3">Heading Medium</option>
          <option value="p">Paragraph</option>
        </select>
        <select onChange={e => { if (e.target.value) { exec('fontSize', e.target.value); e.target.value = ''; } }}
          className="text-xs rounded border border-[hsl(var(--input))] px-1.5 py-1 bg-white" defaultValue=""
          data-testid="editor-fontsize-select">
          <option value="" disabled>Size</option>
          <option value="2">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">Huge</option>
        </select>
        <Separator orientation="vertical" className="h-6 mx-1" />
        <ToolBtn onClick={() => exec('bold')} title="Bold" testid="editor-bold"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} title="Italic" testid="editor-italic"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('underline')} title="Underline" testid="editor-underline"><Underline className="w-4 h-4" /></ToolBtn>
        <Separator orientation="vertical" className="h-6 mx-1" />
        {['#1a2f5e', '#b8860b', '#8b0000', '#1a6b52', '#333333'].map(c => (
          <button key={c} type="button" onMouseDown={e => e.preventDefault()} onClick={() => exec('foreColor', c)}
            className="w-5 h-5 rounded-full border border-white shadow-sm" style={{ background: c }} title={`Text color ${c}`} />
        ))}
        <Separator orientation="vertical" className="h-6 mx-1" />
        <ToolBtn onClick={() => exec('insertUnorderedList')} title="Bullet list" testid="editor-ul"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertOrderedList')} title="Numbered list" testid="editor-ol"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => { const url = window.prompt('Link URL:'); if (url) exec('createLink', url); }} title="Insert link" testid="editor-link"><Link2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => fileInputRef.current?.click()} title="Insert image" testid="editor-image">
          <ImageIcon className={`w-4 h-4 ${uploading ? 'animate-pulse' : ''}`} />
        </ToolBtn>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={() => onChange(sanitizeHtml(editorRef.current?.innerHTML || ''))}
        onPaste={paste}
        onDrop={event => event.preventDefault()}
        onBlur={() => { if (editorRef.current) setSafeEditorHtml(editorRef.current, editorRef.current.innerHTML); }}
        className="min-h-[220px] max-h-[500px] overflow-y-auto p-4 text-sm focus:outline-none prose prose-sm max-w-none [&_img]:max-w-full [&_img]:rounded-lg"
        data-testid="editor-content"
        suppressContentEditableWarning
      />
    </div>
  );
};

/* ============================================================
   NEWSLETTERS ADMIN TAB (admin + chairman)
   ============================================================ */
export function NewslettersAdminTab({ token, role }) {
  const [newsletters, setNewsletters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberError, setSubscriberError] = useState('');
  const [removeSubscriber, setRemoveSubscriber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // list | subscribers

  const load = useCallback(async () => {
    setLoading(true); setSubscriberError('');
    try {
      const [nl, subs] = await Promise.all([
        api.getAuth('/api/admin/newsletters', token),
        api.getAuth('/api/admin/newsletter-subscribers', token)
      ]);
      setNewsletters(nl.newsletters || []);
      setSubscribers(subs.subscribers || []);
    } catch (e) { setSubscriberError(e.message || 'Could not load newsletters and subscribers.'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openEditor = (n = null) => {
    setEditing(n);
    setTitle(n?.title || '');
    setContentHtml(n?.content_html || '');
    setCoverImage(n?.cover_image_url || '');
    setVideoUrl(n?.video_url || '');
    setShowEditor(true);
  };

  const handleSave = async (publish = false) => {
    if (!title.trim()) { toast.error('Please enter a title'); return; }
    setSaving(true);
    try {
      const payload = { title, content_html: contentHtml, cover_image_url: coverImage || null, video_url: videoUrl || null, status: 'draft' };
      let saved;
      if (editing) {
        saved = await api.putAuth(`/api/admin/newsletters/${editing.newsletter_id}`, payload, token);
      } else {
        saved = await api.postAuth('/api/admin/newsletters', payload, token);
      }
      const id = saved.newsletter?.newsletter_id || editing?.newsletter_id;
      if (publish && id) {
        const res = await api.postAuth(`/api/admin/newsletters/${id}/publish`, {}, token);
        toast.success(`Published! ${res.subscribers_notified} subscriber email(s) queued.`);
      } else {
        toast.success(editing ? 'Newsletter updated' : 'Draft saved');
      }
      setShowEditor(false); setEditing(null);
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handlePublishToggle = async (n) => {
    try {
      if (n.status === 'published') {
        await api.postAuth(`/api/admin/newsletters/${n.newsletter_id}/unpublish`, {}, token);
        toast.success('Unpublished');
      } else {
        const res = await api.postAuth(`/api/admin/newsletters/${n.newsletter_id}/publish`, {}, token);
        toast.success(`Published! ${res.subscribers_notified} subscriber email(s) queued.`);
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this newsletter?')) return;
    try { await api.deleteAuth(`/api/admin/newsletters/${id}`, token); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-newsletters">
      <AdminRemovalDialog kind="subscriber" role={role} target={removeSubscriber} token={token} onClose={() => setRemoveSubscriber(null)} onRemoved={id => { setSubscribers(rows => rows.filter(row => row.subscriber_id !== id)); load(); }} />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Newsletters</h2>
          <p className="text-sm text-muted-foreground" data-testid="newsletter-delivery-note">{subscribers.length} listed subscriber(s). Publishing queues messages for active subscribers, subject to email gates and queue pause. Queued does not mean delivered.</p>
        </div>
        <div className="flex gap-2">
          <Button variant={view === 'subscribers' ? 'default' : 'outline'} size="sm" onClick={() => setView(view === 'list' ? 'subscribers' : 'list')} data-testid="newsletters-toggle-subscribers">
            <Mail className="w-4 h-4 mr-1" /> {view === 'list' ? `Subscribers (${subscribers.length})` : 'Back to Newsletters'}
          </Button>
          <Button onClick={() => openEditor()} className="bg-[hsl(var(--primary))] text-white" data-testid="newsletters-create-button">
            <Plus className="w-4 h-4 mr-1" /> New Newsletter
          </Button>
        </div>
      </div>

      {showEditor && (
        <Card className="mb-6" data-testid="newsletter-editor-card">
          <CardHeader><CardTitle style={{ fontFamily: 'Space Grotesk' }}>{editing ? 'Edit Newsletter' : 'Create Newsletter'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Title *</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Newsletter title" data-testid="newsletter-title-input" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Cover Image URL (optional)</Label><Input value={coverImage} onChange={e => setCoverImage(e.target.value)} placeholder="https://... or upload via editor below" /></div>
              <div><Label>Video URL (optional — YouTube or direct link)</Label><Input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." data-testid="newsletter-video-input" /></div>
            </div>
            <div>
              <Label className="mb-2 block">Content (use the toolbar for text sizes, styles & images)</Label>
              <RichTextEditor key={editing?.newsletter_id || 'new'} value={contentHtml} onChange={setContentHtml} token={token} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => handleSave(false)} disabled={saving} variant="outline" data-testid="newsletter-save-draft-button">Save Draft</Button>
              <Button onClick={() => handleSave(true)} disabled={saving} className="bg-[hsl(var(--primary))] text-white" data-testid="newsletter-publish-button">
                <Send className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Publish & Notify Subscribers'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowEditor(false); setEditing(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {subscriberError ? <Card data-testid="newsletter-list-error"><CardContent className="p-4 space-y-2"><p role="alert" className="text-sm text-destructive">{subscriberError}</p><Button variant="outline" className="min-h-11" onClick={load} data-testid="newsletter-list-retry">Retry</Button></CardContent></Card> : loading ? <Skeleton className="h-48 rounded-xl" data-testid="newsletter-list-loading" /> : view === 'subscribers' ? (
        <Card data-testid="newsletter-subscribers-card">
          <CardHeader><CardTitle className="text-base">Newsletter Subscribers ({subscribers.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm gsn-operations-table sm:table-fixed" data-testid="newsletter-subscribers-table">
                <thead><tr className="border-b">
                  <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Email</th>
                  <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Status</th>
                  <th className="text-left p-3 font-medium text-[hsl(var(--muted-foreground))]">Subscribed On</th>
                  {role === 'admin' && <th className="text-left p-3 font-medium text-muted-foreground">Actions</th>}
                </tr></thead>
                <tbody>
                  {subscribers.map(s => (
                    <tr key={s.subscriber_id || s._id} className="border-b hover:bg-[hsl(var(--muted))]" data-testid={`subscriber-row-${s.subscriber_id}`}>
                      <td className="p-3 break-all" data-testid={`subscriber-email-${s.subscriber_id}`}>{s.email}</td>
                      <td className="p-3"><Badge variant={s.status === 'active' ? 'default' : 'secondary'} className="text-xs">{s.status}</Badge></td>
                      <td className="p-3 text-[hsl(var(--muted-foreground))]">{s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB') : '-'}</td>
                      {role === 'admin' && <td className="p-3"><Button variant="outline" size="sm" className="min-h-11 text-destructive border-destructive/30" disabled={!s.subscriber_id} onClick={() => setRemoveSubscriber(s)} data-testid={`subscriber-remove-${s.subscriber_id}`} aria-label={`Delete subscriber ${s.email}`}><Trash2 className="w-4 h-4 mr-1" />Delete</Button></td>}
                    </tr>
                  ))}
                  {subscribers.length === 0 && <tr><td colSpan={role === 'admin' ? 4 : 3} className="p-8 text-muted-foreground" data-testid="newsletter-subscribers-empty">No subscribers currently listed. People can join using the website’s newsletter form.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="space-y-3">
          {newsletters.length === 0 ? (
            <Card className="text-center p-8"><p className="text-[hsl(var(--muted-foreground))]">No newsletters yet. Create your first one!</p></Card>
          ) : newsletters.map(n => (
            <Card key={n.newsletter_id}>
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{n.title}</h3>
                    <Badge variant={n.status === 'published' ? 'default' : 'secondary'} className="text-xs">{n.status}</Badge>
                  </div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                    By {n.created_by} · {n.published_at ? `Published ${new Date(n.published_at).toLocaleDateString('en-GB')}` : `Created ${new Date(n.created_at).toLocaleDateString('en-GB')}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => handlePublishToggle(n)} data-testid={`newsletter-toggle-${n.newsletter_id}`}>
                    {n.status === 'published' ? <><EyeOff className="w-3 h-3 mr-1" /> Unpublish</> : <><Send className="w-3 h-3 mr-1" /> Publish</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditor(n)}><Edit className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="text-[hsl(var(--destructive))]" onClick={() => handleDelete(n.newsletter_id)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   STAFF USER MANAGEMENT (admin only)
   ============================================================ */
export function UsersTab({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'committee' });

  const load = useCallback(async () => {
    try { const d = await api.getAuth('/api/admin/users', token); setUsers(d.users || []); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('Please fill all fields'); return; }
    try {
      await api.postAuth('/api/admin/users', form, token);
      toast.success(`${form.role} account created`);
      setShowForm(false); setForm({ email: '', name: '', password: '', role: 'committee' });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.putAuth(`/api/admin/users/${u.admin_id}`, { status: u.status === 'disabled' ? 'active' : 'disabled' }, token);
      toast.success(u.status === 'disabled' ? 'Account enabled' : 'Account disabled');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete account ${u.email}?`)) return;
    try { await api.deleteAuth(`/api/admin/users/${u.admin_id}`, token); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message); }
  };

  const roleColors = { admin: 'bg-red-100 text-red-800', treasurer: 'bg-green-100 text-green-800', chairman: 'bg-blue-100 text-blue-800', committee: 'bg-amber-100 text-amber-800' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Staff Accounts ({users.length})</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[hsl(var(--primary))] text-white" data-testid="users-create-button">
          <Plus className="w-4 h-4 mr-1" /> Add Account
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base">Create Staff Account</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><Label>Password *</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
              <div>
                <Label>Role *</Label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-lg border border-[hsl(var(--input))] px-3 py-2 text-sm bg-transparent" data-testid="user-role-select">
                  <option value="admin">Admin (full access)</option>
                  <option value="treasurer">Treasurer (finance)</option>
                  <option value="chairman">Chairman (oversight + content)</option>
                  <option value="committee">Committee (uploads only)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} className="bg-[hsl(var(--primary))] text-white" data-testid="user-save-button">Create Account</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="space-y-3">
          {users.map(u => (
            <Card key={u.admin_id}>
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{u.name}</span>
                    <Badge className={`text-xs capitalize ${roleColors[u.role] || ''}`}>{u.role || 'admin'}</Badge>
                    {u.status === 'disabled' && <Badge variant="destructive" className="text-xs">Disabled</Badge>}
                  </div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))]">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(u)} data-testid={`user-toggle-${u.email}`}>
                    {u.status === 'disabled' ? 'Enable' : 'Disable'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-[hsl(var(--destructive))]" onClick={() => handleDelete(u)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ACTIVITY LOG (admin only, with hide/unhide)
   ============================================================ */
export function ActivityTab({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHidden, setShowHidden] = useState(false);
  const [accountsOnly, setAccountsOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api.getAuth(`/api/admin/activity?show_hidden=${showHidden}`, token); setLogs(d.logs || []); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token, showHidden]);
  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (log) => {
    try { await api.putAuth(`/api/admin/activity/${log.log_id}/toggle-hidden`, {}, token); load(); }
    catch (e) { toast.error(e.message); }
  };

  const ACCOUNT_ACTIONS = ['login', 'user_created', 'user_updated', 'user_deleted'];
  const visibleLogs = accountsOnly
    ? logs.filter(l => l.entity === 'users' || ACCOUNT_ACTIONS.includes(l.action))
    : logs;

  const roleColors = { admin: 'text-red-700', treasurer: 'text-green-700', chairman: 'text-blue-700', committee: 'text-amber-700' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Activity Log</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">See which account did what changes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={accountsOnly ? 'default' : 'outline'} size="sm" onClick={() => setAccountsOnly(!accountsOnly)} data-testid="activity-accounts-filter">
            <UsersIcon className="w-4 h-4 mr-1" /> {accountsOnly ? 'Showing account activity' : 'Account activity only'}
          </Button>
          <Button variant="outline" size="icon" onClick={() => setShowHidden(!showHidden)} title={showHidden ? 'Hide hidden entries' : 'Show hidden entries'} data-testid="activity-toggle-hidden">
            {showHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="space-y-2">
          {visibleLogs.length === 0 && <Card className="text-center p-8"><p className="text-[hsl(var(--muted-foreground))]">No activity {accountsOnly ? 'for accounts' : 'yet'}</p></Card>}
          {visibleLogs.map(log => (
            <Card key={log.log_id} className={log.is_hidden ? 'opacity-50' : ''}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Activity className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm">
                      <span className={`font-semibold capitalize ${roleColors[log.actor_role] || ''}`}>{log.actor_name || log.actor_email}</span>
                      <span className="text-[hsl(var(--muted-foreground))]"> ({log.actor_role})</span>
                      <span className="mx-1">—</span>
                      <span>{log.details || log.action}</span>
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(log.created_at).toLocaleString('en-GB')} · {log.entity}</div>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => toggleHidden(log)} data-testid={`activity-hide-${log.log_id}`}>
                  {log.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   EXPENSES TAB (all roles can upload)
   ============================================================ */
export function ExpensesTab({ token, role }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', amount: '', description: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState(emptyDateRange());
  const canNotify = role === 'admin' || role === 'treasurer';
  const [showNotify, setShowNotify] = useState(false);
  const [notifyEmails, setNotifyEmails] = useState([]);
  const [notifyInput, setNotifyInput] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  useEffect(() => {
    if (!canNotify) return;
    api.getAuth('/api/admin/expenses/notify-settings', token).then(d => setNotifyEmails(d.emails || [])).catch(() => {});
  }, [canNotify, token]);
  const saveNotify = async (emails) => {
    setNotifySaving(true);
    try { const d = await api.putAuth('/api/admin/expenses/notify-settings', { emails }, token); setNotifyEmails(d.emails || []); toast.success(d.message || 'Saved'); return true; }
    catch (e) { toast.error(e.message); return false; }
    finally { setNotifySaving(false); }
  };
  const addNotify = async () => {
    const em = notifyInput.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast.error('Please enter a valid email address'); return; }
    if (notifyEmails.includes(em)) { toast.error('That address is already on the list'); return; }
    if (await saveNotify([...notifyEmails, em])) setNotifyInput('');
  };
  const shown = expenses.filter(e => inDateRange(e.created_at, range));
  const shownTotal = shown.reduce((a, e) => a + Number(e.amount || 0), 0);
  const exportShown = () => downloadCsv(`expenses_${rangeSlug(range)}`, [
    { key: 'expense_id', label: 'Reference' }, { key: 'title', label: 'Title' }, { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount (GBP)', format: e => fmtMoney(e.amount) }, { key: 'submitted_by_name', label: 'Submitted by', format: e => e.submitted_by_name || e.submitted_by || '' },
    { key: 'submitted_by_role', label: 'Role' }, { key: 'receipt_url', label: 'Receipt', format: e => (e.receipt_url ? 'Yes' : 'No') }, { key: 'created_at', label: 'Date', format: e => fmtDateGB(e.created_at) },
  ], shown);

  const load = useCallback(async () => {
    try { const d = await api.getAuth('/api/admin/expenses', token); setExpenses(d.expenses || []); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Please enter a title'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('amount', form.amount);
      fd.append('description', form.description);
      if (file) fd.append('receipt_file', file);
      await api.uploadAuth('/api/admin/expenses', fd, token);
      toast.success('Expense uploaded');
      setShowForm(false); setForm({ title: '', amount: '', description: '' }); setFile(null);
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await api.deleteAuth(`/api/admin/expenses/${id}`, token); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
        <div className="flex-1">
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Expenses ({shown.length}{range.preset !== 'all' ? ` of ${expenses.length}` : ''}) <span className="text-sm font-normal text-[hsl(var(--muted-foreground))]" data-testid="expenses-total">· £{shownTotal.toFixed(2)} · {describeDateRange(range)}</span></h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Upload receipts (images or PDF) for reimbursement / records</p>
        </div>
        <DateRangeFilter value={range} onChange={setRange} label="Expense date" testId="expenses-date" />
        <Button variant="outline" size="sm" onClick={exportShown} disabled={!shown.length} data-testid="expenses-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        {canNotify && <Button variant="outline" size="sm" onClick={() => setShowNotify(!showNotify)} data-testid="expenses-notify-toggle"><Mail className="w-4 h-4 mr-1" /> Email notifications{notifyEmails.length ? ` (${notifyEmails.length})` : ''}</Button>}
        <Button onClick={() => setShowForm(!showForm)} className="bg-[hsl(var(--primary))] text-white" data-testid="expenses-create-button">
          <Plus className="w-4 h-4 mr-1" /> Upload Expense
        </Button>
      </div>

      {canNotify && showNotify && (
        <Card className="mb-6" data-testid="expenses-notify-card">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Email notifications</CardTitle>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Whenever anyone uploads an expense, these addresses get an email with the title, amount, who uploaded it and a link to the Staff Portal.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {notifyEmails.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))]">No addresses yet — nobody is emailed about new expenses.</p>}
            {notifyEmails.map(em => (
              <div key={em} className="flex items-center justify-between gap-2" data-testid="expenses-notify-row">
                <span className="text-sm break-all">{em}</span>
                <Button variant="ghost" size="sm" disabled={notifySaving} onClick={() => saveNotify(notifyEmails.filter(x => x !== em))} aria-label={`Remove ${em}`} data-testid="expenses-notify-remove"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2 pt-1">
              <Input type="email" value={notifyInput} onChange={e => setNotifyInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addNotify(); }} placeholder="treasurer@example.com" className="flex-1 min-w-[200px]" data-testid="expenses-notify-input" />
              <Button variant="outline" onClick={addNotify} disabled={notifySaving || !notifyInput.trim()} data-testid="expenses-notify-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="w-4 h-4" /> New Expense</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Venue decorations" data-testid="expense-title-input" /></div>
              <div><Label>Amount (£)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="expense-amount-input" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional details" /></div>
            <div>
              <Label>Receipt (image or PDF, max 30MB)</Label>
              <Input type="file" accept="image/*,.pdf" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="expense-file-input" />
              {file && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{file.name}</p>}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saving} className="bg-[hsl(var(--primary))] text-white" data-testid="expense-submit-button">{saving ? 'Uploading...' : 'Submit Expense'}</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="space-y-3">
          {shown.length === 0 && <Card className="text-center p-8" data-testid="expenses-empty"><p className="text-[hsl(var(--muted-foreground))]">{expenses.length ? 'No expenses in this date range' : 'No expenses uploaded yet'}</p></Card>}
          {shown.map(exp => (
            <Card key={exp.expense_id}>
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{exp.title}</span>
                    {exp.amount && <Badge className="text-xs">£{exp.amount}</Badge>}
                    <Badge variant="secondary" className="text-xs capitalize">{exp.submitted_by_role}</Badge>
                  </div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))]">
                    {exp.description && <span>{exp.description} · </span>}
                    By {exp.submitted_by_name || exp.submitted_by} · {new Date(exp.created_at).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {exp.receipt_url && (
                    <a href={`${BACKEND_URL}${exp.receipt_url}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" data-testid={`expense-receipt-${exp.expense_id}`}><FileText className="w-3 h-3 mr-1" /> Receipt</Button>
                    </a>
                  )}
                  {(role === 'admin' || role === 'treasurer') && (
                    <Button size="sm" variant="outline" className="text-[hsl(var(--destructive))]" onClick={() => handleDelete(exp.expense_id)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   MINUTES OF MEETINGS TAB (all roles)
   ============================================================ */
export function MinutesTab({ token, role }) {
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', meeting_date: '', notes: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const d = await api.getAuth('/api/admin/minutes', token); setMinutes(d.minutes || []); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Please enter a title'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('meeting_date', form.meeting_date);
      fd.append('notes', form.notes);
      if (file) fd.append('minutes_file', file);
      await api.uploadAuth('/api/admin/minutes', fd, token);
      toast.success('Minutes uploaded');
      setShowForm(false); setForm({ title: '', meeting_date: '', notes: '' }); setFile(null);
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete these minutes?')) return;
    try { await api.deleteAuth(`/api/admin/minutes/${id}`, token); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Minutes of Meetings ({minutes.length})</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Upload meeting minutes (PDF, DOC, DOCX)</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[hsl(var(--primary))] text-white" data-testid="minutes-create-button">
          <Plus className="w-4 h-4 mr-1" /> Upload Minutes
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> New Minutes</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Committee Meeting Jan 2025" data-testid="minutes-title-input" /></div>
              <div><Label>Meeting Date</Label><Input type="date" value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })} data-testid="minutes-date-input" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional summary" /></div>
            <div>
              <Label>Minutes File (PDF/DOC/DOCX, max 30MB)</Label>
              <Input type="file" accept=".pdf,.doc,.docx,image/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="minutes-file-input" />
              {file && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{file.name}</p>}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={saving} className="bg-[hsl(var(--primary))] text-white" data-testid="minutes-submit-button">{saving ? 'Uploading...' : 'Upload Minutes'}</Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="animate-pulse h-48 bg-[hsl(var(--muted))] rounded-xl" /> : (
        <div className="space-y-3">
          {minutes.length === 0 && <Card className="text-center p-8"><p className="text-[hsl(var(--muted-foreground))]">No minutes uploaded yet</p></Card>}
          {minutes.map(m => (
            <Card key={m.minutes_id}>
              <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{m.title}</span>
                    {m.meeting_date && <Badge variant="outline" className="text-xs"><Calendar className="w-3 h-3 mr-1" />{m.meeting_date}</Badge>}
                    <Badge variant="secondary" className="text-xs capitalize">{m.uploaded_by_role}</Badge>
                  </div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))]">
                    {m.notes && <span>{m.notes} · </span>}
                    By {m.uploaded_by_name || m.uploaded_by} · {new Date(m.created_at).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {m.file_url && (
                    <a href={`${BACKEND_URL}${m.file_url}`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline"><FileText className="w-3 h-3 mr-1" /> View File</Button>
                    </a>
                  )}
                  {role === 'admin' && (
                    <Button size="sm" variant="outline" className="text-[hsl(var(--destructive))]" onClick={() => handleDelete(m.minutes_id)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   MEMBERSHIP APPLICATIONS TAB (admin + chairman)
   ============================================================ */
export function ApplicationsTab({ token, role }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waiveApp, setWaiveApp] = useState(null);          // application being waived (existing member)
  const [waiveReason, setWaiveReason] = useState('');
  const [waiveConfirm, setWaiveConfirm] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [waiveError, setWaiveError] = useState('');
  const cancelRef = useRef(null);

  const waivePayment = async () => {
    if (!waiveApp) return;
    setWaiving(true);
    try {
      const r = await api.postAuth(`/api/admin/membership-applications/${waiveApp.application_id}/override-approve`, { reason: waiveReason.trim(), confirm: waiveConfirm }, token);
      toast.message(`${r.message} — ${r.membership_number}`);
      setWaiveApp(null); setWaiveReason(''); setWaiveConfirm(false);
      load();
    } catch (e) { setWaiveError(e.message); }
    finally { setWaiving(false); }
  };

  const load = useCallback(async () => {
    try { const d = await api.getAuth('/api/admin/membership-applications', token); setApps(d.applications || []); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (app, status) => {
    try {
      await api.putAuth(`/api/admin/membership-applications/${app.application_id}/status`, { status }, token);
      toast.success(`Application ${status}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const viewProof = async (app) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/membership-applications/${app.application_id}/proof`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not load document');
      }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }} data-testid="applications-title">Membership Applications ({apps.length})</h2>
      </div>
      <p className="mb-4 text-sm text-muted-foreground" data-testid="applications-payment-policy">Website applicants require confirmed payment. Only Admin can override an existing migrated membership with an individual invitation recorded as sent. No new membership number or renewal is created by an override.</p>
      <Dialog open={!!waiveApp} onOpenChange={(o) => { if (!o && !waiving) { setWaiveApp(null); setWaiveConfirm(false); setWaiveError(''); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onOpenAutoFocus={e => { e.preventDefault(); cancelRef.current?.focus(); }} data-testid="override-approve-alert-dialog">
          <DialogHeader>
            <DialogTitle data-testid="override-approve-title">Override & approve existing membership</DialogTitle>
            <DialogDescription data-testid="override-approve-description">This is an Admin-only approval of the original migrated member. Their GS number, type and expiry stay unchanged. No payment is taken and no email is sent.</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2 text-sm break-words" data-testid="override-approve-summary-card">
            <p className="font-semibold" data-testid="override-approve-member-name">{waiveApp?.migration_override?.member?.full_name}</p>
            <p className="tabular-nums break-all" data-testid="override-approve-member-number">{waiveApp?.migration_override?.member?.membership_number}</p>
            <p data-testid="override-approve-member-type">Type: {waiveApp?.migration_override?.member?.membership_type}</p>
            <p className="tabular-nums" data-testid="override-approve-member-expiry">Existing expiry: {waiveApp?.migration_override?.member?.expires_at ? new Date(waiveApp.migration_override.member.expires_at).toLocaleDateString('en-GB') : 'No expiry (lifetime / senior)'}</p>
            <Badge variant="outline" className="whitespace-normal border-[hsl(var(--gs-warning))]" data-testid="override-approve-payment-label">{waiveApp?.confirmed_payment ? 'Payment already confirmed — no new charge' : 'Payment waived — existing member'}</Badge>
            <p className="text-xs text-muted-foreground" data-testid="override-approve-card-policy">{waiveApp?.migration_override?.card_blockers?.length ? 'Card will remain pending: ' + waiveApp.migration_override.card_blockers.join(', ').replaceAll('_', ' ') : 'Required details are complete. The digital card can be issued without sending an email.'}</p>
          </div>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="waive-reason">Audit reason (required, at least 10 characters)</Label>
              <Textarea id="waive-reason" value={waiveReason} onChange={e => { setWaiveReason(e.target.value); setWaiveError(''); }} maxLength={2000} disabled={waiving} aria-describedby={waiveError ? 'override-reason-error' : undefined} placeholder="Explain why this invited migrated member should be approved" data-testid="override-approve-audit-reason-textarea" />
            </div>
            <label className="flex items-center gap-3 min-h-11 text-sm cursor-pointer" data-testid="override-approve-confirm-label">
              <Checkbox checked={waiveConfirm} disabled={waiving} onCheckedChange={v => setWaiveConfirm(v === true)} data-testid="override-approve-confirm-checkbox" />
              <span>I confirm this is the existing migrated member and their individual invitation was sent.</span>
            </label>
            {waiveError && <p id="override-reason-error" role="alert" className="text-sm text-destructive" data-testid="override-approve-audit-reason-error">{waiveError}</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button ref={cancelRef} variant="outline" className="min-h-11" disabled={waiving} onClick={() => setWaiveApp(null)} data-testid="override-approve-cancel-button">Cancel</Button>
            <Button className="min-h-11" onClick={waivePayment} disabled={!waiveConfirm || waiveReason.trim().length < 10 || waiving} data-testid="override-approve-confirm-button"><BadgeCheck className="w-4 h-4 mr-2" />{waiving ? 'Approving…' : 'Confirm override approval'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {loading ? <Skeleton className="h-48 rounded-xl" data-testid="applications-loading" /> : (
        <div className="space-y-3">
          {apps.length === 0 && <Card className="text-center p-8"><p className="text-[hsl(var(--muted-foreground))]">No applications yet</p></Card>}
          {apps.map(a => (
            <Card key={a.application_id} data-testid={`application-${a.application_id}`}>
              <CardContent className="p-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold break-words" data-testid={`application-name-${a.application_id}`}>{a.full_name}</span>
                    <Badge variant="outline" className="text-xs capitalize">{(a.membership_type || '').replace('_', ' ')}</Badge>
                    <Badge variant={a.status === 'approved' ? 'default' : a.status === 'rejected' ? 'destructive' : 'secondary'} className="text-xs">{a.status}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground break-words">
                    <span className="break-all" data-testid={`application-email-${a.application_id}`}>{a.email}</span> · <span className="tabular-nums" data-testid={`application-number-${a.application_id}`}>{a.membership_number || a.migration_override?.member?.membership_number || 'no number yet'}</span>
                    <div className="mt-1" data-testid={`application-payment-status-${a.application_id}`}>
                      {a.display_payment_status === 'waived_existing' ? <Badge variant="outline" className="whitespace-normal border-[hsl(var(--gs-warning))]" data-testid={`application-payment-waived-${a.application_id}`}>Payment waived — existing member</Badge> : a.display_payment_status === 'paid' ? 'Payment confirmed' : a.display_payment_status === 'not_required_staff' ? 'Payment not required — staff-created' : 'Payment confirmation required'}
                      {a.display_payment_status !== 'waived_existing' && a.amount != null && Number.isFinite(Number(a.amount)) && <span className="tabular-nums"> · £{Number(a.amount).toFixed(2)}</span>}
                    </div>
                    {a.migration_override_approval?.card_status && <p className="text-xs mt-1" data-testid={`application-card-status-${a.application_id}`}>Digital card: {a.migration_override_approval.card_status}</p>}
                    {a.status !== 'approved' && !a.migration_override?.eligible && <p className="text-xs mt-1" data-testid={`override-approve-ineligible-hint-${a.application_id}`}>{a.migration_override?.reason}</p>}
                    {a.occupation && <span> · Occupation: {a.occupation}</span>}
                    {a.interests && <span> · Interests: {a.interests}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {a.proof_file_url && (
                    <Button size="sm" variant="outline" className="min-h-11" onClick={() => viewProof(a)} data-testid={`app-view-proof-${a.application_id}`}>
                      <FileText className="w-3 h-3 mr-1" /> View ID
                    </Button>
                  )}
                  {role === 'admin' && a.status !== 'approved' && a.migration_override?.eligible && (
                    <Button size="sm" variant="outline" className="min-h-11" onClick={() => { setWaiveApp(a); setWaiveReason(''); setWaiveConfirm(false); setWaiveError(''); }} data-testid={`application-override-approve-${a.application_id}`}>
                      <BadgeCheck className="w-3 h-3 mr-1" /> Override & approve
                    </Button>
                  )}
                  {a.status !== 'approved' && !a.migration_override?.is_migrated && (
                    <Button size="sm" variant="outline" className="min-h-11" disabled={!a.confirmed_payment && a.display_payment_status !== 'not_required_staff'} onClick={() => setStatus(a, 'approved')} data-testid={`app-approve-${a.application_id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  {a.status !== 'rejected' && (
                    <Button size="sm" variant="outline" className="min-h-11 text-[hsl(var(--destructive))]" onClick={() => setStatus(a, 'rejected')} data-testid={`app-reject-${a.application_id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* Add an existing (pre-system) member directly: no payment, 1-year validity, source = existing_member */
function ExistingMemberDialog({ open, onClose, token, onCreated }) {
  const empty = { full_name: '', email: '', phone: '', dob: '', gender: '', address: '', postcode: '', membership_type: '', reason: '' };
  const [f, setF] = useState(empty);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get('/api/membership/pricing').then(d => {
      const ps = (d.pricing || []).filter(p => p.visible !== false);
      setPlans(ps);
      setF(prev => ({ ...prev, membership_type: prev.membership_type || (ps.find(p => p.type === 'standard') || ps[0] || {}).type || '' }));
    }).catch(() => {});
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    if (!confirm) { toast.error('Please confirm this person is an existing paid member'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      ['full_name', 'email', 'phone', 'dob', 'gender', 'address', 'postcode', 'membership_type'].forEach(k => fd.append(k, f[k] || ''));
      fd.append('existing_member', 'true');
      fd.append('waiver_reason', f.reason || '');
      const r = await api.uploadAuth('/api/membership/apply', fd, token);
      toast.success(`${r.message} — ${r.membership_number}`);
      setF(empty); setConfirm(false); onClose(); onCreated && onCreated();
    } catch (err) { toast.error(err.message || 'Could not register member'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="existing-member-dialog">
        <DialogHeader>
          <DialogTitle>Add existing member (no payment)</DialogTitle>
          <DialogDescription>For members who already paid before the online system. No Stripe charge; a membership number is issued now and the membership is valid for <strong>1 year from today</strong>.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Full name *</Label><Input required value={f.full_name} onChange={e => setF({ ...f, full_name: e.target.value })} data-testid="existing-member-name" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Email *</Label><Input type="email" required value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="existing-member-email" /></div>
            <div><Label>Phone *</Label><Input type="tel" required value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="existing-member-phone" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Date of birth *</Label><Input type="date" required value={f.dob} onChange={e => setF({ ...f, dob: e.target.value })} data-testid="existing-member-dob" /></div>
            <div>
              <Label>Membership type *</Label>
              <select required className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" value={f.membership_type} onChange={e => setF({ ...f, membership_type: e.target.value })} data-testid="existing-member-type">
                {plans.map(p => <option key={p.type} value={p.type}>{p.label || p.type}</option>)}
              </select>
            </div>
          </div>
          <AddressLookup address={f.address} postcode={f.postcode} onChange={({ address, postcode }) => setF(prev => ({ ...prev, address, postcode }))} testIdPrefix="existing-member" />
          <div><Label>Note for audit log (optional)</Label><Input value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} placeholder="e.g. on 2024 paper register" data-testid="existing-member-reason" /></div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox checked={confirm} onCheckedChange={v => setConfirm(!!v)} className="mt-0.5" data-testid="existing-member-confirm" />
            <span>I confirm this person is an existing paid member of Gujarat Samaj Nottingham</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !confirm} data-testid="existing-member-submit"><BadgeCheck className="w-4 h-4 mr-2" />{saving ? 'Registering…' : 'Register for 1 year'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   CAMERA QR SCANNER TAB — Gate Mode
   Continuous scanning, event filter, fullscreen colour flash,
   sound cues. Works on iOS/Android browsers (installable PWA).
   ============================================================ */
const SCAN_COLORS = {
  blue: { bg: 'rgba(23, 64, 158, 0.96)', label: 'MEMBER' },
  amber: { bg: 'rgba(217, 119, 6, 0.96)', label: 'NON-MEMBER' },
  orange: { bg: 'rgba(217, 119, 6, 0.96)', label: 'NON-MEMBER' },
  yellow: { bg: 'rgba(202, 168, 4, 0.97)', label: 'CHILD / STUDENT' },
  green: { bg: 'rgba(202, 168, 4, 0.97)', label: 'CHILD / STUDENT' },
  red: { bg: 'rgba(190, 24, 34, 0.97)', label: 'STOP' },
};

function playScanSound(valid) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (valid) {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.09);
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.28);
      osc.start(); osc.stop(ctx.currentTime + 0.3);
    } else {
      osc.type = 'square';
      osc.frequency.setValueAtTime(160, ctx.currentTime);
      gain.gain.setValueAtTime(0.5, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.75);
      osc.start(); osc.stop(ctx.currentTime + 0.8);
    }
  } catch { /* audio not available */ }
}

export function CameraScannerTab({ token, role = 'admin' }) {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [flash, setFlash] = useState(null); // {color, result}
  const [manualId, setManualId] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(() => new URLSearchParams(window.location.search).get('event') || '');
  const [scanCount, setScanCount] = useState(0);
  const scannerRef = useRef(null);
  const lastScanRef = useRef(0);
  const selectedEventRef = useRef('');

  useEffect(() => { selectedEventRef.current = selectedEvent; }, [selectedEvent]);

  useEffect(() => {
    api.get('/api/events?include_demo=1').then(d => setEvents(d.events || [])).catch(() => {});
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  const processTicket = async (ref) => {
    if (!selectedEventRef.current) { toast.error('Select the event before checking in tickets'); return; }
    try {
      // Backend resolves ticket_id / secure token / verify URL / legacy JSON; event_id enables WRONG EVENT detection
      const res = await api.postAuth('/api/tickets/scan', { ticket_id: ref.trim(), event_id: selectedEventRef.current || undefined }, token);
      let color = res.scan_color || (res.status === 'valid' ? 'amber' : 'red');
      const wrongEvent = res.status === 'wrong_event';
      const valid = res.status === 'valid';
      if (!valid) color = 'red';
      playScanSound(valid);
      setScanCount(c => c + (valid ? 1 : 0));
      setResult({ ...res, wrongEvent });
      setFlash({ color, result: { ...res, wrongEvent } });
      setTimeout(() => setFlash(null), 2200);
    } catch (e) { toast.error(e.message); }
  };

  const processMembershipQr = async (memberToken) => {
    try {
      const v = await api.get(`/api/verify/member/${memberToken}`);
      const ok = v.status === 'ACTIVE';
      playScanSound(ok);
      const res = {
        status: ok ? 'valid' : 'invalid', result: `MEMBERSHIP ${v.status}`, kind: 'membership',
        message: ok ? `Member verified · ${v.full_name}` : `Membership ${v.status.toLowerCase()}`,
        attendee_name: v.full_name || '', ticket_type: v.membership_type ? `${v.membership_type} member` : 'Membership',
        membership_number: v.membership_number, scan_color: ok ? 'blue' : 'red', is_member: ok,
      };
      setResult(res);
      setFlash({ color: ok ? 'blue' : 'red', result: res });
      setTimeout(() => setFlash(null), 2200);
    } catch (e) { toast.error(e.message); }
  };

  const onScanSuccess = (decodedText) => {
    const now = Date.now();
    if (now - lastScanRef.current < 2500) return;
    lastScanRef.current = now;
    const memberMatch = /\/verify\/member\/([A-Za-z0-9_-]+)/.exec(decodedText || '');
    if (memberMatch) { processMembershipQr(memberMatch[1]); return; }
    let ref = decodedText;
    try {
      const parsed = JSON.parse(decodedText);
      if (parsed.ticket_id) ref = parsed.ticket_id;
    } catch { /* raw ticket id / token / URL */ }
    processTicket(ref);
  };

  const startCamera = async () => {
    setResult(null);
    // IMPORTANT (iOS Safari): the camera container must be VISIBLE with real dimensions
    // BEFORE the camera starts, otherwise the video renders at 0x0 and nothing displays/decodes.
    setScanning(true);
    await new Promise(r => setTimeout(r, 80)); // let the container paint first
    try {
      const scanner = new Html5Qrcode('qr-camera-region');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 12, qrbox: (vw, vh) => { const s = Math.min(vw, vh) * 0.75; return { width: s, height: s }; } },
        onScanSuccess,
        () => {}
      );
    } catch (e) {
      setScanning(false);
      toast.error('Camera access failed. Please allow camera permission in your browser, or use manual entry below.');
      scannerRef.current = null;
    }
  };

  const stopCamera = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch {}
      try { scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const fl = flash ? SCAN_COLORS[flash.color] || SCAN_COLORS.red : null;

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Fullscreen colour flash overlay */}
      {flash && fl && (
        <div className="fixed inset-0 z-[300] flex flex-col items-center justify-center text-white p-6"
          style={{ background: fl.bg }} onClick={() => setFlash(null)} data-testid="scan-flash-overlay">
          {flash.result?.status === 'valid' && !flash.result?.wrongEvent ? (
            <CheckCircle className="w-24 h-24 mb-4" />
          ) : (
            <XCircle className="w-24 h-24 mb-4" />
          )}
          <div className="text-4xl font-black tracking-wide mb-1" style={{ fontFamily: 'Space Grotesk' }}>
            {flash.result?.kind === 'membership' ? flash.result.result : flash.result?.wrongEvent ? 'WRONG EVENT' : flash.result?.status === 'wrong_day' ? 'WRONG DAY' : flash.result?.status === 'valid' ? `ADMIT ${flash.result?.admit_count || 1}` : flash.result?.status === 'already_used' ? 'ALREADY SCANNED' : flash.result?.status === 'cancelled' ? 'CANCELLED' : flash.result?.status === 'refunded' ? 'REFUNDED' : 'NOT VALID'}
          </div>
          <div className="text-lg font-bold opacity-90 mb-2">{fl.label}</div>
          {flash.result?.attendees?.length > 0 && (
            <div className="text-center text-sm opacity-90 max-w-xs">
              {flash.result.attendees.map((a, i) => (
                <div key={stableRecordKey(a)}>{a.name} <span className="opacity-70">({a.category_name})</span></div>
              ))}
            </div>
          )}
          {flash.result?.event_day && (
            <div className={`mt-2 ${flash.result?.status === 'wrong_day' ? 'text-xl font-bold' : 'text-sm opacity-80'}`} data-testid="scan-flash-event-day">
              {flash.result?.status === 'wrong_day' ? 'Ticket is for: ' : 'Valid for: '}
              {new Date(flash.result.event_day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          )}
          <div className="mt-4 text-xs opacity-70">Tap anywhere to dismiss — scanner is ready</div>
        </div>
      )}

      <div className="text-center">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Gate QR Scanner</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Continuous scanning — screen flashes <span className="font-semibold text-blue-700">blue (member)</span>, <span className="font-semibold text-orange-600">orange (non-member)</span>, <span className="font-semibold text-yellow-600">yellow (child/student)</span> or <span className="font-semibold text-red-600">red (invalid)</span> with sound.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div>
            <Label>Scanning for event</Label>
            <select value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}
              className="w-full rounded-lg border border-[hsl(var(--input))] px-3 py-2 text-sm bg-transparent mt-1" data-testid="scanner-event-select">
              <option value="">Select the event before ticket check-in</option>
              {events.map(ev => <option key={ev.event_id} value={ev.event_id}>{ev.title}</option>)}
            </select>
            {selectedEvent && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Tickets from other events will flash RED as "Wrong Event".</p>}
          </div>

          <div
            id="qr-camera-region"
            className={`rounded-xl overflow-hidden bg-black ${scanning ? 'block min-h-[300px]' : 'hidden'}`}
            data-testid="qr-camera-region"
          />
          {!scanning ? (
            <Button onClick={startCamera} className="w-full h-12 bg-[hsl(var(--primary))] text-white" data-testid="scanner-start-camera-button">
              <Camera className="w-5 h-5 mr-2" /> Start Gate Scanning
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-green-700">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Scanning live — {scanCount} admitted this session
              </div>
              <Button onClick={stopCamera} variant="outline" className="w-full h-12" data-testid="scanner-stop-camera-button">
                <StopCircle className="w-5 h-5 mr-2" /> Stop Camera
              </Button>
            </div>
          )}

          <Separator />
          <div>
            <Label>Or enter Ticket ID manually</Label>
            <div className="flex gap-2 mt-1">
              <Input value={manualId} onChange={e => setManualId(e.target.value)} placeholder="tkt_xxxxxxxxxxxx"
                onKeyDown={e => e.key === 'Enter' && manualId.trim() && processTicket(manualId)}
                data-testid="scanner-manual-input" />
              <Button variant="outline" onClick={() => manualId.trim() && processTicket(manualId)} data-testid="scanner-manual-button">
                <QrCode className="w-4 h-4 mr-1" /> Check
              </Button>
            </div>
          </div>

          {result && !flash && (
            <div className={`p-4 rounded-xl border-2 ${result.status === 'valid' && !result.wrongEvent ? 'border-green-500 bg-green-50' : result.status === 'already_used' ? 'border-yellow-500 bg-yellow-50' : 'border-red-500 bg-red-50'}`} data-testid="scanner-result">
              <div className="flex items-center gap-2 mb-2">
                {result.status === 'valid' && !result.wrongEvent ? <CheckCircle className="w-6 h-6 text-green-600" /> : result.status === 'already_used' ? <AlertTriangle className="w-6 h-6 text-yellow-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
                <span className="text-lg font-bold uppercase" style={{ fontFamily: 'Space Grotesk' }}>
                  {result.result || (result.wrongEvent ? 'Wrong event' : result.status?.replace('_', ' '))}
                </span>
                {result.admit_count > 1 && <Badge>Admit {result.admit_count}</Badge>}
              </div>
              <p className="text-sm mb-1">{result.message}</p>
              {result.attendees?.length > 0 ? (
                <div className="text-sm space-y-0.5">
                  {result.attendees.map((a, i) => <div key={stableRecordKey(a)}><strong>{a.name}</strong> — {a.category_name}{a.gender ? ` (${a.gender === 'prefer_not_to_say' ? 'prefer not to say' : a.gender})` : ''}</div>)}
                </div>
              ) : result.attendee_name && <p className="text-sm"><strong>Name:</strong> {result.attendee_name}</p>}
              {result.event_day && <p className="text-sm mt-1"><strong>Valid for day:</strong> {new Date(result.event_day + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</p>}
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => setResult(null)}>Clear result</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[hsl(var(--muted)/0.4)]">
        <CardContent className="p-4 text-xs text-[hsl(var(--muted-foreground))] space-y-1">
          <p className="font-semibold text-[hsl(var(--foreground))]">Install as an app (recommended for gate staff):</p>
          <p><strong>iPhone:</strong> Open this Scanner page in Safari → Share button → "Add to Home Screen"</p>
          <p><strong>Android:</strong> Chrome menu (⋮) → "Add to Home screen" / "Install app"</p>
          <p>The <strong>GS Scanner</strong> icon then opens straight into this QR scanner, full-screen like a native app, always connected live to the ticket database. (You will be asked to log in the first time.)</p>
          <p className="text-amber-700"><strong>Already installed before?</strong> Delete the old home-screen icon first, then add it again from this page — old icons keep the old settings.</p>
        </CardContent>
      </Card>

      {/* Admin-only: share GS QR Scanner access with gate volunteers */}
      {role === 'admin' && <ScannerAccessPanel token={token} />}
    </div>
  );
}

/* ============================================================
   AI ASSISTANT TAB (admin only) — GPT-5 via Emergent key
   Theme switching + analytics Q&A
   ============================================================ */
export function AssistantTab({ token, role = 'admin' }) {
  const [scope, setScope] = useState(role === 'admin' ? 'full' : 'finance');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: role === 'admin'
      ? "Namaste! I'm your portal assistant. Ask me about revenue, tickets, donations, members, volunteers or events — or tell me to change the website theme (Diwali, Holi, Navratri, Uttarayan, Gujarat Day, Independence Day, Republic Day, or back to Default)."
      : "Namaste! I'm your finance assistant. Ask me about payments, refunds, ticket sales, donations and Saturday Club fees. I can't see member personal details or change the website." }
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [sessionId] = useState(() => `assist_${Date.now().toString(36)}`);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, thinking]);
  useEffect(() => {
    api.getAuth('/api/admin/assistant/scope', token).then(d => {
      setScope(d.scope);
      if (d.intro) setMessages([{ role: 'assistant', content: d.intro }]);
    }).catch(() => {});
  }, [token]);

  const SUGGESTIONS = scope === 'full' ? [
    'Switch theme to Diwali',
    'How much did we take this month?',
    'Which event has sold the most tickets?',
    'How many members expire in the next 30 days?',
    'Set theme back to default'
  ] : [
    'How much did we take this month?',
    'Total refunds so far and why?',
    'How many tickets are still unscanned?',
    'Saturday Club fees paid vs pending?',
    'Net revenue by service'
  ];

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || thinking) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setThinking(true);
    try {
      const res = await api.postAuth('/api/admin/assistant', { message: msg, session_id: sessionId }, token);
      setMessages(prev => [...prev, { role: 'assistant', content: res.reply, action: res.action }]);
      if (res.action?.type === 'set_theme') {
        toast.success(`Website theme changed to ${res.action.theme.replace('_', ' ')}! Open the site to see it live.`);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry, something went wrong: ${e.message}` }]);
    }
    setThinking(false);
  };

  return (
    <div className="max-w-2xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
      <div className="mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <Sparkle className="w-5 h-5 text-[hsl(var(--primary))]" /> AI Portal Assistant
        </h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{scope === 'full' ? 'Powered by GPT-5 — can change site themes and analyse your live data' : 'Powered by GPT-5 — finance & ticket insight only (read-only)'}</p>
      </div>

      <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
          {messages.map((m, i) => (
            <div key={stableRecordKey(m)} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[hsl(var(--primary))] text-white rounded-br-sm' : 'bg-[hsl(var(--muted))] rounded-bl-sm'}`}
                data-testid={`assistant-msg-${i}`}>
                {m.content}
                {m.action?.type === 'set_theme' && (
                  <div className="mt-2 text-xs font-semibold flex items-center gap-1 opacity-80">
                    <CheckCircle className="w-3.5 h-3.5" /> Theme applied: {m.action.theme.replace('_', ' ')}
                  </div>
                )}
              </div>
            </div>
          ))}
          {thinking && (
            <div className="flex justify-start">
              <div className="bg-[hsl(var(--muted))] rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[hsl(var(--muted-foreground))] animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-[hsl(var(--muted-foreground))] animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 rounded-full bg-[hsl(var(--muted-foreground))] animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-[hsl(var(--border))] p-3 space-y-2">
          <div className="flex gap-1.5 flex-wrap">
            {SUGGESTIONS.map(s => (
              <button key={s} onClick={() => send(s)} disabled={thinking}
                className="text-xs px-2.5 py-1 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50"
                data-testid={`assistant-suggestion-${s.slice(0, 12).replace(/\s/g, '-')}`}>
                {s}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} placeholder={scope === 'full' ? "Ask about revenue, events... or say 'switch theme to Holi'" : "Ask about payments, refunds, tickets or Saturday Club fees"}
              onKeyDown={e => e.key === 'Enter' && send()} disabled={thinking} data-testid="assistant-input" />
            <Button onClick={() => send()} disabled={thinking || !input.trim()} className="bg-[hsl(var(--primary))] text-white" data-testid="assistant-send-button">
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center">Each question incurs a small server charge.</p>
        </div>
      </Card>
    </div>
  );
}

/* ============================================================
   SCANNER ACCESS PANEL — Admin shares GS QR Scanner logins
   with gate volunteers; revocable after the event.
   ============================================================ */
export function ScannerAccessPanel({ token }) {
  const [accounts, setAccounts] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const d = await api.getAuth('/api/admin/scanner-access', token); setAccounts(d.accounts || []); }
    catch { /* non-critical */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!email.includes('@')) { toast.error('Enter a valid email address'); return; }
    setBusy(true);
    try {
      const res = await api.postAuth('/api/admin/scanner-access', { email: email.trim(), password: password.trim() || null }, token);
      toast.success(res.message);
      setEmail(''); setPassword('');
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const revoke = async (a) => {
    setBusy(true);
    try {
      const res = await api.deleteAuth(`/api/admin/scanner-access/${a.admin_id}`, token);
      toast.success(res.message);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Card data-testid="scanner-access-panel">
      <CardHeader>
        <CardTitle className="text-lg" style={{fontFamily: 'Space Grotesk'}}>Share GS QR Scanner Access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Give gate volunteers a scanner-only login — they can ONLY scan tickets, nothing else.
          Login details are emailed to them automatically. Remove access after the event with one click.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input type="email" placeholder="Volunteer's email" value={email} onChange={e => setEmail(e.target.value)} className="flex-1" data-testid="scanner-access-email-input" />
          <Input placeholder="Password (blank = auto-generate)" value={password} onChange={e => setPassword(e.target.value)} className="sm:w-56" data-testid="scanner-access-password-input" />
          <Button onClick={create} disabled={busy} data-testid="scanner-access-create-button">Create & Email</Button>
        </div>
        {accounts.length > 0 && (
          <div className="space-y-2">
            {accounts.map(a => (
              <div key={a.admin_id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg border border-[hsl(var(--border))]" data-testid={`scanner-access-row-${a.admin_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{a.email}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">
                    Password: <span className="font-mono">{a.display_password || '(hidden)'}</span>
                    {a.created_at && <> · added {new Date(a.created_at).toLocaleDateString('en-GB')}</>}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => revoke(a)} disabled={busy} data-testid={`scanner-access-revoke-${a.admin_id}`}>
                  Remove Access
                </Button>
              </div>
            ))}
          </div>
        )}
        {accounts.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]">No scanner-only accounts yet.</p>}
      </CardContent>
    </Card>
  );
}
